/**
 * Sprint 10 P0: TreatX Admin - Clinic CRM Package Management Page
 * P2.3b: Full i18n coverage
 * 
 * PURPOSE: Manage clinic CRM subscription packages (Trial, Starter, Pro, Enterprise)
 * - Create new packages
 * - Edit existing packages (version auto-increments)
 * - Activate/Deactivate packages
 * - Make packages public/private
 * 
 * This page manages CATALOG packages, NOT individual subscriptions.
 * Changes here immediately sync to Clinic Admin package selection page.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLocalizedField } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import { usePackageCatalog } from '../../context/PackageCatalogContext';
import { FEATURE_DEFINITIONS, ENFORCED_FEATURE_KEYS, SERVICE_METADATA_KEYS, formatPrice } from '../../config/packages';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Package, ChevronLeft, Edit2, Trash2, Plus, Save, X,
  Eye, EyeOff, Zap, Power, Check, AlertTriangle, RefreshCw, Tag
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';

// Package Editor Modal
const FEATURE_I18N = {
  priority_support: 'prioritySupport', dedicated_support: 'dedicatedSupport',
  campaigns_enabled: 'campaigns', leads_enabled: 'leadsManagement',
  campaigns: 'campaigns', leads_management: 'leadsManagement',
};

// TREATX-IYZICO-P0F7-A3 — market VAT configuration, inside the EXISTING price
// manager (no new screen, no second pricing authority). The rate belongs to the
// canonical price book; the amount above stays VAT-EXCLUSIVE and the backend
// computes gross = net + VAT with this approved rate.
const MarketVatSection = ({ book, onSaved }) => {
  const { t } = useTranslation();
  const [rate, setRate] = useState('');
  const [treatment, setTreatment] = useState('STANDARD');
  const [basis, setBasis] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRate(book?.tax_rate_bps === null || book?.tax_rate_bps === undefined ? '' : String(book.tax_rate_bps));
    setTreatment(book?.tax_treatment || 'STANDARD');
    setBasis(book?.tax_legal_basis || '');
  }, [book?.market, book?.tax_rate_bps, book?.tax_treatment, book?.tax_legal_basis]);

  if (!book) return null;
  const configured = book.tax_rate_bps !== null && book.tax_rate_bps !== undefined;

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/pricing/price-books', {
        market: book.market, currency: book.currency,
        country_codes: book.country_codes || [], display_name: book.display_name || {},
        active: book.active !== false,
        tax_rate_bps: rate === '' ? null : Number(rate),
        tax_treatment: treatment,
        tax_legal_basis: basis || null,
      });
      toast({ title: t('packageCatalog.prices.vatSaved', 'VAT configuration saved') });
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast({ title: t('common.error', 'Error'), description: (d && (d.message || d)) || e?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-3 border-t space-y-2" data-testid="market-vat-section">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold">
          {t('packageCatalog.prices.vatTitle', 'Market VAT')} · {book.market}
        </Label>
        <span className={`text-xs ${configured ? 'text-green-700' : 'text-amber-600'}`} data-testid="market-vat-status">
          {configured
            ? `${(book.tax_rate_bps / 100).toFixed(2)}% · ${book.tax_treatment || 'STANDARD'}`
            : t('packageCatalog.prices.vatUnconfigured', 'Not configured — sales fail closed')}
        </span>
      </div>
      <p className="text-xs text-gray-500">{t('packageCatalog.prices.vatHint', 'Enter the rate your accountant approved, in basis points (2000 = 20%). Prices above are VAT-exclusive; VAT is added on top. A 0% rate is not an exemption — record the approved treatment with its legal basis instead.')}</p>
      {/* TAX-1 — a SUGGESTION only. It is never persisted or activated by
          rendering: it fills the inputs so an authorised Super Admin still has
          to review and press save. Domestic TR only; other markets get a
          pending/legal-basis prompt instead of a guessed rate. */}
      {!configured && (
        book.market === 'TR' ? (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 space-y-1" data-testid="market-vat-suggestion">
            <div>
              <span className="font-semibold">{t('packageCatalog.prices.vatSuggested', 'Suggested')}:</span>{' '}
              STANDARD · 2000 bps (20%) ·{' '}
              <span className="font-semibold text-amber-700" data-testid="market-vat-suggestion-status">
                {t('packageCatalog.prices.vatNotSaved', 'NOT SAVED / NOT APPROVED')}
              </span>
            </div>
            <div>{t('packageCatalog.prices.vatSuggestedHint', 'Your accountant must confirm the rate before you save it. Nothing is applied until you press save.')}</div>
            <Button size="sm" variant="ghost" className="h-7 px-2" data-testid="market-vat-use-suggestion-btn"
              onClick={() => { setRate('2000'); setTreatment('STANDARD'); }}>
              {t('packageCatalog.prices.vatUseSuggestion', 'Fill suggested values')}
            </Button>
          </div>
        ) : (
          <div className="text-xs bg-gray-50 border rounded p-2" data-testid="market-vat-international-pending">
            {t('packageCatalog.prices.vatInternationalPending', 'International VAT treatment is PENDING REVIEW — rate UNCONFIGURED, legal basis REQUIRED. No exemption is applied automatically.')}
          </div>
        )
      )}
      <div className="grid grid-cols-3 gap-2 items-end">
        <div>
          <Label className="text-xs">{t('packageCatalog.prices.vatRate', 'Rate (bps)')}</Label>
          <Input type="number" min="0" max="10000" step="1" value={rate} data-testid="market-vat-rate-input"
            onChange={(e) => setRate(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t('packageCatalog.prices.vatTreatment', 'Treatment')}</Label>
          <select className="w-full border rounded h-9 text-sm px-2" data-testid="market-vat-treatment-select"
            value={treatment} onChange={(e) => setTreatment(e.target.value)}>
            <option value="STANDARD">STANDARD</option>
            <option value="EXEMPT">EXEMPT</option>
            <option value="REVERSE_CHARGE">REVERSE_CHARGE</option>
            <option value="OUT_OF_SCOPE">OUT_OF_SCOPE</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">{t('packageCatalog.prices.vatBasis', 'Legal basis')}</Label>
          <Input value={basis} data-testid="market-vat-basis-input" placeholder={t('packageCatalog.prices.vatBasisPh', 'Required for non-STANDARD')}
            onChange={(e) => setBasis(e.target.value)} />
        </div>
      </div>
      <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid="market-vat-save-btn">
        {saving ? t('common.saving', 'Saving…') : t('packageCatalog.prices.vatSave', 'Save VAT configuration')}
      </Button>
    </div>
  );
};

// CANONICAL SSOT: Admin CommercialPrice manager. Publishes market-specific
// prices (the ONE commercial authority). PSP-neutral — no Stripe/currency logic.
const PriceManagerModal = ({ pkg, packageId, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [prices, setPrices] = useState([]);
  const [books, setBooks] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ market: '', amount: '', billing_interval: 'monthly' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, bRes] = await Promise.all([
        api.get(`/admin/pricing/packages/${packageId}/prices`),
        api.get('/admin/pricing/price-books'),
      ]);
      setPrices(pRes.data?.prices || []);
      setCoverage(pRes.data?.coverage || []);
      setBooks(bRes.data?.price_books || []);
      if (!form.market && (bRes.data?.price_books || []).length) {
        setForm((f) => ({ ...f, market: bRes.data.price_books[0].market }));
      }
    } catch (e) {
      toast({ title: t('common.error', 'Error'), description: e?.message || 'load failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [packageId]);

  const publish = async () => {
    // ZERO-PRICE-UPGRADE-01 — an explicitly entered 0 is a VALID published
    // price. Empty input, NaN and negative amounts stay rejected, as does a
    // missing market. (The canonical API accepts amount 0; only this form
    // was blocking it.)
    const raw = typeof form.amount === 'string' ? form.amount.trim() : form.amount;
    const amt = raw === '' || raw === null || raw === undefined ? NaN : Number(raw);
    if (!form.market) {
      toast({ title: t('common.error', 'Error'), description: t('packageCatalog.prices.marketRequired', 'Select a market'), variant: 'destructive' });
      return;
    }
    if (!Number.isFinite(amt) || amt < 0) {
      toast({ title: t('common.error', 'Error'), description: t('packageCatalog.prices.amountInvalid', 'Enter a valid amount of 0 or more'), variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/pricing/prices', {
        package_id: packageId, market: form.market, amount: amt,
        billing_interval: form.billing_interval, publish: true,
      });
      toast({ title: t('packageCatalog.prices.published', 'Price published') });
      setForm((f) => ({ ...f, amount: '' }));
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast({ title: t('common.error', 'Error'), description: e?.response?.data?.detail || e?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AlertDialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <AlertDialogContent className="max-w-lg z-[10000]" data-testid="price-manager-modal">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('packageCatalog.prices.title', 'Commercial Prices')} — {getLocalizedField(pkg.name, 'en')}</AlertDialogTitle>
          <AlertDialogDescription>{t('packageCatalog.prices.desc', 'Market-specific published prices are the single source of commercial truth. A market with no published price is fail-closed (not purchasable).')}</AlertDialogDescription>
        </AlertDialogHeader>

        {loading ? <p className="text-sm text-gray-500">{t('common.loading', 'Loading…')}</p> : (
          <div className="space-y-4">
            <div className="space-y-1" data-testid="price-coverage-list">
              {coverage.map((c) => {
                const p = prices.find((x) => x.market === c.market && x.state === 'published');
                return (
                  <div key={c.market} className="flex items-center justify-between text-sm border-b py-1">
                    <span className="font-medium">{c.market}/{c.currency}</span>
                    {c.has_published_price && p
                      ? <span className="text-green-700" data-testid={`published-price-${c.market}`}>{new Intl.NumberFormat(undefined, { style: 'currency', currency: c.currency }).format(p.amount)} · v{p.price_version}</span>
                      : <span className="text-amber-600" data-testid={`no-price-${c.market}`}>{t('packageCatalog.prices.none', 'Not published')}</span>}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-3 gap-2 items-end pt-2 border-t">
              <div>
                <Label className="text-xs">{t('packageCatalog.prices.market', 'Market')}</Label>
                <select className="w-full border rounded h-9 text-sm px-2" data-testid="price-market-select"
                  value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })}>
                  {books.map((b) => <option key={b.market} value={b.market}>{b.market} ({b.currency})</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">{t('packageCatalog.prices.amount', 'Amount')}</Label>
                <Input type="number" min="0" step="0.01" value={form.amount} data-testid="price-amount-input"
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{t('packageCatalog.prices.interval', 'Interval')}</Label>
                <select className="w-full border rounded h-9 text-sm px-2" data-testid="price-interval-select"
                  value={form.billing_interval} onChange={(e) => setForm({ ...form, billing_interval: e.target.value })}>
                  <option value="monthly">monthly</option>
                  <option value="yearly">yearly</option>
                </select>
              </div>
            </div>

            <MarketVatSection
              book={books.find((b) => b.market === form.market)}
              onSaved={load}
            />
          </div>
        )}

        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="price-manager-close">{t('common.close', 'Close')}</Button>
          <Button onClick={publish} disabled={saving} data-testid="publish-price-btn">
            {saving ? t('common.saving', 'Saving…') : t('packageCatalog.prices.publish', 'Publish price')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};


/** PKG-LIMIT-FIDELITY-01 — a blank quota field means "not supplied" (null),
 *  never 0. `-1` (unlimited) and `0` (none) are both preserved verbatim. */
const _limitInput = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return '';
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? '' : parsed;
};

const PackageEditorModal = ({ pkg, onSave, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    id: pkg?.id || '',
    displayName: pkg?.displayName || '',
    description: pkg?.description || '',
    pricing: {
      amount: pkg?.pricing?.amount || 0,
      currency: pkg?.pricing?.currency || 'EUR',
      interval: pkg?.pricing?.interval || 'month',
    },
    // PKG-LIMIT-FIDELITY-01 — the editor holds NO default business value.
    // An unset field stays blank and is sent as `null`, which the canonical
    // package writer resolves to the already-configured value (update) or to
    // the canonical `PackageLimits` default (create). `-1` = unlimited and
    // `0` = none are both preserved verbatim.
    limits: {
      leads_per_month: pkg?.limits?.leads_per_month ?? '',
      max_users: pkg?.limits?.max_users ?? pkg?.limits?.sales_reps ?? '',
      offers_per_month: pkg?.limits?.offers_per_month ?? '',
      quotes_monthly_max: pkg?.limits?.quotes_monthly_max ?? '',
      active_threads_max: pkg?.limits?.active_threads_max ?? '',
      campaigns_active_max: pkg?.limits?.campaigns_active_max ?? '',
      campaigns_create_monthly_max: pkg?.limits?.campaigns_create_monthly_max ?? '',
    },
    features: {
      priority_support: pkg?.features?.priority_support ?? false,
      dedicated_support: pkg?.features?.dedicated_support ?? false,
      campaigns_enabled: pkg?.features?.campaigns_enabled ?? false,
      leads_enabled: pkg?.features?.leads_enabled ?? false,
      // TREATX-PAYMENT-SAFETY-01 — editor-owned per-package capability.
      patient_payments_enabled: pkg?.features?.patient_payments_enabled ?? false,
    },
    upgradePrompt: pkg?.upgradePrompt || '',
    public: pkg?.public ?? true,
    active: pkg?.active ?? true,
    sortOrder: pkg?.sortOrder ?? pkg?.upgrade_order ?? 99,
    requires_contact_sales: pkg?.requires_contact_sales ?? pkg?.requiresContactSales ?? false,
  });

  // TREATX-PAYMENT-SAFETY-01 — confirm on SAVE only when the patient online
  // payment capability actually changed (not merely when the toggle is
  // clicked). Toggle mutates only local edit state; nothing persists until the
  // confirmation is accepted, which then flows through the SAME canonical save
  // path (onSave). Cancel keeps the modal open with nothing persisted.
  const originalPatientPayments = Boolean(pkg?.features?.patient_payments_enabled);
  const [paymentConfirm, setPaymentConfirm] = useState(null); // null | 'enable' | 'disable'

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = Boolean(formData.features?.patient_payments_enabled);
    if (next !== originalPatientPayments) {
      setPaymentConfirm(next ? 'enable' : 'disable');
      return;
    }
    onSave(formData);
  };

  const handleFeatureToggle = (featureKey) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [featureKey]: !prev.features[featureKey]
      }
    }));
  };

  // PKG-EDIT-RENDER-FIX-01: the price-book currency chips below read the
  // canonical price-book registry — the SAME source the Manage Prices modal
  // uses. Previously `books` was referenced but never defined in this
  // component, throwing `ReferenceError: books is not defined` on open and
  // crashing the whole Catalog tab (caught by the tab error boundary as
  // "This panel failed to render"). Source it locally; read-only, display only.
  const [books, setBooks] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get('/admin/pricing/price-books')
      .then((r) => { if (alive) setBooks(r.data?.price_books || []); })
      .catch(() => { if (alive) setBooks([]); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">
            {pkg ? t('packageCatalog.editor.editTitle', { name: pkg.displayName }) : t('packageCatalog.editor.createTitle')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="displayName">{t('packageCatalog.editor.name')}</Label>
              <Input
                id="displayName"
                value={formData.displayName}
                onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder={t('packageCatalog.editor.phName')}
                required
              />
            </div>
            <div>
              <Label htmlFor="id">{t('packageCatalog.editor.packageId')}</Label>
              <Input
                id="id"
                value={formData.id}
                onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value.toUpperCase().replace(/\s+/g, '_') }))}
                placeholder={t('packageCatalog.editor.phId')}
                disabled={!!pkg}
                required
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="description">{t('packageCatalog.editor.description')}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('packageCatalog.editor.phDesc')}
              rows={2}
            />
          </div>

          {/* Pricing — CANONICAL: commercial prices live ONLY in market price books
              (Phase 1 Provider Pricing SSOT). The former amount/currency/interval
              editor here was a ghost projection (backend ignores embedded pricing)
              and hard-coded EUR/USD/GBP, hiding TRY. Currencies below are read from
              the canonical price-book registry. */}
          <div className="border rounded-lg p-4 bg-gray-50" data-testid="package-editor-pricing-note">
            <h3 className="font-semibold mb-2">{t('packageCatalog.editor.pricing')}</h3>
            <p className="text-sm text-gray-600">{t('packageCatalog.editor.pricingCanonicalNote')}</p>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="package-editor-pricebook-currencies">
              {books.map((b) => (
                <span key={b.market} className="text-xs px-2 py-1 rounded-full bg-white border border-gray-300 text-gray-700" data-testid={`pricebook-currency-${b.market}`}>
                  {b.market} · {b.currency}
                </span>
              ))}
            </div>
          </div>

          {/* Limits */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="font-semibold mb-3">{t('packageCatalog.editor.usageLimits')}</h3>
            <p className="text-xs text-gray-500 mb-3">{t('packageCatalog.editor.unlimitedHint')}</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="leads_per_month">{t('packageCatalog.editor.maxLeads')}</Label>
                <Input
                  id="leads_per_month"
                  type="number"
                  data-testid="editor-limit-leads-per-month"
                  value={formData.limits.leads_per_month}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    limits: { ...prev.limits, leads_per_month: _limitInput(e.target.value) }
                  }))}
                />
              </div>
              <div>
                <Label htmlFor="max_users">{t('packageCatalog.editor.maxUsers')}</Label>
                <Input
                  id="max_users"
                  type="number"
                  data-testid="editor-limit-max-users"
                  value={formData.limits.max_users}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    limits: { ...prev.limits, max_users: _limitInput(e.target.value) }
                  }))}
                />
              </div>
              <div>
                <Label htmlFor="offers_per_month">{t('packageCatalog.editor.offersPerMonth')}</Label>
                <Input
                  id="offers_per_month"
                  type="number"
                  data-testid="editor-limit-offers-per-month"
                  value={formData.limits.offers_per_month}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    limits: { ...prev.limits, offers_per_month: _limitInput(e.target.value) }
                  }))}
                />
              </div>
              <div>
                <Label htmlFor="quotes_monthly_max">{t('packageCatalog.editor.quotesPerMonth', 'Quote requests / month')}</Label>
                <Input
                  id="quotes_monthly_max"
                  type="number"
                  data-testid="editor-limit-quotes-monthly-max"
                  value={formData.limits.quotes_monthly_max}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    limits: { ...prev.limits, quotes_monthly_max: _limitInput(e.target.value) }
                  }))}
                />
              </div>
              {/* PKG-LIMIT-FIDELITY-01 — these three quotas were previously
                  unreachable from this editor and were overwritten by the API
                  writer on every save (active threads → 5, campaigns → 0). */}
              <div>
                <Label htmlFor="active_threads_max">{t('packageCatalog.editor.activeThreadsMax', 'Active threads')}</Label>
                <Input
                  id="active_threads_max"
                  type="number"
                  data-testid="editor-limit-active-threads-max"
                  value={formData.limits.active_threads_max}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    limits: { ...prev.limits, active_threads_max: _limitInput(e.target.value) }
                  }))}
                />
              </div>
              <div>
                <Label htmlFor="campaigns_active_max">{t('packageCatalog.editor.campaignsActiveMax', 'Active campaigns')}</Label>
                <Input
                  id="campaigns_active_max"
                  type="number"
                  data-testid="editor-limit-campaigns-active-max"
                  value={formData.limits.campaigns_active_max}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    limits: { ...prev.limits, campaigns_active_max: _limitInput(e.target.value) }
                  }))}
                />
              </div>
              <div>
                <Label htmlFor="campaigns_create_monthly_max">{t('packageCatalog.editor.campaignsCreateMonthlyMax', 'Campaigns created / month')}</Label>
                <Input
                  id="campaigns_create_monthly_max"
                  type="number"
                  data-testid="editor-limit-campaigns-create-monthly-max"
                  value={formData.limits.campaigns_create_monthly_max}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    limits: { ...prev.limits, campaigns_create_monthly_max: _limitInput(e.target.value) }
                  }))}
                />
              </div>
            </div>
          </div>

          {/* Features — PACKAGE-ENTITLEMENT-SSOT-REMEDIATION: only truthful,
              technically-enforced software capabilities appear here. */}
          <div className="border rounded-lg p-4 bg-gray-50" data-testid="editor-enforced-capabilities">
            <h3 className="font-semibold mb-1">{t('packageCatalog.editor.enforcedCapabilities', 'Enforced capabilities')}</h3>
            <p className="text-xs text-gray-500 mb-3">{t('packageCatalog.editor.enforcedCapabilitiesHint', 'Backend-enforced software capabilities.')}</p>
            <div className="grid grid-cols-3 gap-3">
              {ENFORCED_FEATURE_KEYS.map((key) => (
                <div key={key} className="flex items-center gap-2">
                  <Switch
                    id={key}
                    checked={formData.features[key] ?? false}
                    onCheckedChange={() => handleFeatureToggle(key)}
                    data-testid={`editor-feature-${key}`}
                  />
                  <Label htmlFor={key} className="text-sm cursor-pointer">
                    {t(`packageCatalog.features.${FEATURE_I18N[key] || key}`, FEATURE_DEFINITIONS[key]?.name || key)}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Commercial service metadata — NOT technically enforced. */}
          <div className="border rounded-lg p-4 bg-gray-50" data-testid="editor-service-metadata">
            <h3 className="font-semibold mb-1">{t('packageCatalog.editor.serviceMetadata', 'Commercial service metadata')}</h3>
            <p className="text-xs text-gray-500 mb-3">{t('packageCatalog.editor.serviceMetadataHint', 'Marketed service levels. These are commercial commitments, not software-enforced gates.')}</p>
            <div className="grid grid-cols-3 gap-3">
              {SERVICE_METADATA_KEYS.map((key) => (
                <div key={key} className="flex items-center gap-2">
                  <Switch
                    id={key}
                    checked={formData.features[key] ?? false}
                    onCheckedChange={() => handleFeatureToggle(key)}
                    data-testid={`editor-feature-${key}`}
                  />
                  <Label htmlFor={key} className="text-sm cursor-pointer">
                    {t(`packageCatalog.features.${FEATURE_I18N[key] || key}`, FEATURE_DEFINITIONS[key]?.name || key)}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* TREATX-PAYMENT-SAFETY-01 — per-package patient online payment
              capability. Editor-owned so Super Admin can enable it
              parametrically. Kept OUT of the shared FEATURE_DEFINITIONS list so
              it never surfaces on provider-facing package/upgrade cards. */}
          <div className="border rounded-lg p-4 bg-gray-50" data-testid="editor-patient-payments-block">
            <div className="flex items-center gap-2">
              <Switch
                id="patient_payments_enabled"
                checked={formData.features.patient_payments_enabled ?? false}
                onCheckedChange={(checked) => setFormData(prev => ({
                  ...prev,
                  features: { ...prev.features, patient_payments_enabled: checked },
                }))}
                data-testid="editor-patient-payments-toggle"
              />
              <Label htmlFor="patient_payments_enabled" className="cursor-pointer">
                {t('packageCatalog.editor.patientPaymentsToggle', 'Patient online payments')}
              </Label>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {t('packageCatalog.editor.patientPaymentsHint', 'When off, real patient online checkout is blocked (fail-closed) for providers on this package. When on, checkout reaches the existing payment-readiness layer.')}
            </p>
          </div>

          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="font-semibold mb-3">{t('packageCatalog.editor.visibilityStatus')}</h3>
            <div className="flex gap-8">
              <div className="flex items-center gap-2">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                />
                <Label htmlFor="active">{t('packageCatalog.editor.activeToggle')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="public"
                  checked={formData.public}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, public: checked }))}
                />
                <Label htmlFor="public">{t('packageCatalog.editor.publicToggle')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="requires_contact_sales"
                  data-testid="editor-requires-contact-sales"
                  checked={formData.requires_contact_sales}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_contact_sales: checked }))}
                />
                <Label htmlFor="requires_contact_sales">{t('packageCatalog.editor.requiresContactSales', 'Requires Contact Sales')}</Label>
              </div>
            </div>
            <div className="mt-4 max-w-xs">
              <Label htmlFor="sortOrder">{t('packageCatalog.editor.upgradeOrder', 'Upgrade Order')}</Label>
              <Input
                id="sortOrder"
                type="number"
                data-testid="editor-upgrade-order"
                value={formData.sortOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>

          {/* Upgrade Prompt */}
          <div>
            <Label htmlFor="upgradePrompt">{t('packageCatalog.editor.upgradePrompt')}</Label>
            <Input
              id="upgradePrompt"
              value={formData.upgradePrompt}
              onChange={(e) => setFormData(prev => ({ ...prev, upgradePrompt: e.target.value }))}
              placeholder={t('packageCatalog.editor.phUpgrade')}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('packageCatalog.editor.cancel')}
            </Button>
            <Button type="submit" className="bg-gradient-to-r from-purple-600 to-pink-600">
              <Save className="h-4 w-4 mr-2" />
              {pkg ? t('packageCatalog.editor.save') : t('packageCatalog.editor.create')}
            </Button>
          </div>
        </form>
      </div>

      {/* TREATX-PAYMENT-SAFETY-01 — save-time confirmation, only when the
          patient online payment capability changed. Accept → canonical save. */}
      <AlertDialog open={!!paymentConfirm} onOpenChange={(o) => { if (!o) setPaymentConfirm(null); }}>
        <AlertDialogContent className="z-[10001]" data-testid="patient-payments-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`packageCatalog.editor.patientPaymentsConfirm.${paymentConfirm || 'enable'}.title`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(`packageCatalog.editor.patientPaymentsConfirm.${paymentConfirm || 'enable'}.body`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="patient-payments-confirm-cancel">
              {t('packageCatalog.editor.patientPaymentsConfirm.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="patient-payments-confirm-accept"
              onClick={() => { setPaymentConfirm(null); onSave(formData); }}
            >
              {t(`packageCatalog.editor.patientPaymentsConfirm.${paymentConfirm || 'enable'}.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Main Page Component
const ClinicCRMPackageManagementPage = ({ embedded = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { 
    getAllPackages, 
    getAvailablePackages,
    createPackage, 
    updatePackage, 
    activatePackage, 
    deactivatePackage,
    makePackagePublic,
    makePackagePrivate,
    resetToDefaults,
    setCatalogScope,
    refreshCatalog,
    lastSync
  } = usePackageCatalog();
  
  const [editModal, setEditModal] = useState({ open: false, pkg: null });
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [filter, setFilter] = useState('all'); // all | public | hidden | inactive
  const [confirmAction, setConfirmAction] = useState(null); // { type, pkg }
  const [priceCoverage, setPriceCoverage] = useState({}); // package_key -> coverage
  const [pricesModal, setPricesModal] = useState({ open: false, pkg: null });

  // Canonical CommercialPrice coverage per market (SSOT). The Package itself no
  // longer carries a price — market prices are published as CommercialPrices.
  const fetchCoverage = React.useCallback(async () => {
    try {
      const res = await api.get('/admin/pricing/coverage');
      setPriceCoverage(res.data?.coverage || {});
    } catch (e) {
      console.log('[PackageCatalog] price coverage fetch failed:', e?.message);
    }
  }, []);
  useEffect(() => { fetchCoverage(); }, [fetchCoverage]);

  // PKG-CATALOG-ADMIN-VISIBILITY-01: the admin surface must load ALL packages
  // (incl. hidden/inactive) so a hidden package never becomes unmanageable.
  useEffect(() => {
    setCatalogScope('admin');
    refreshCatalog();
    return () => {
      setCatalogScope('public');
      refreshCatalog();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allPackages = getAllPackages();
  const availablePackages = getAvailablePackages();

  const displayedPackages = allPackages.filter((pkg) => {
    if (filter === 'public') return pkg.active && pkg.public;
    if (filter === 'hidden') return !pkg.public;
    if (filter === 'inactive') return !pkg.active;
    return true;
  });

  const handleSavePackage = async (formData) => {
    try {
      if (editModal.pkg) {
        await updatePackage(formData.id, formData);
        toast({
          title: t('packageCatalog.editor.toastUpdated'),
          description: t('packageCatalog.editor.savedDesc', { name: formData.displayName }),
        });
      } else {
        await createPackage(formData);
        toast({
          title: t('packageCatalog.editor.toastCreated'),
          description: t('packageCatalog.editor.savedDesc', { name: formData.displayName }),
        });
      }
      setEditModal({ open: false, pkg: null });
    } catch (err) {
      toast({
        title: t('packageCatalog.editor.toastSaveFailed'),
        description: err.message || t('packageCatalog.editor.saveFailedDesc'),
        variant: 'destructive',
      });
    }
  };

  const requestToggleActive = (pkg) =>
    setConfirmAction({ type: pkg.active ? 'deactivate' : 'activate', pkg });
  const requestTogglePublic = (pkg) =>
    setConfirmAction({ type: pkg.public ? 'hide' : 'show', pkg });

  const executeConfirm = async () => {
    if (!confirmAction) return;
    const { type, pkg } = confirmAction;
    try {
      if (type === 'deactivate') {
        await deactivatePackage(pkg.id);
        toast({ title: t('packageCatalog.toast.deactivated') });
      } else if (type === 'activate') {
        await activatePackage(pkg.id);
        toast({ title: t('packageCatalog.toast.activated') });
      } else if (type === 'hide') {
        await makePackagePrivate(pkg.id);
        toast({ title: t('packageCatalog.toast.hidden') });
      } else if (type === 'show') {
        await makePackagePublic(pkg.id);
        toast({ title: t('packageCatalog.toast.shown') });
      }
    } catch (err) {
      toast({ title: t('packageCatalog.toast.failed'), description: err.message || '', variant: 'destructive' });
    } finally {
      setConfirmAction(null);
    }
  };

  const handleResetToDefaults = () => {
    resetToDefaults();
    setShowConfirmReset(false);
    toast({
      title: t('packageCatalog.reset.toast'),
      description: t('packageCatalog.reset.body'),
    });
  };

  return (
    <div className={embedded ? 'bg-transparent' : 'min-h-screen bg-gradient-to-br from-slate-50 to-purple-50'}>
      <Toaster />
      
      {/* Header — hidden when embedded inside the merged Packages admin page */}
      {!embedded && (
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/treatx-admin/dashboard')}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {t('packageCatalog.header.back')}
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Package className="h-5 w-5 text-purple-600" />
                  {t('packageCatalog.header.title')}
                </h1>
                <p className="text-sm text-gray-500">
                  {t('packageCatalog.header.lastSync')}: {new Date(lastSync).toLocaleTimeString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowConfirmReset(true)}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                {t('packageCatalog.header.reset')}
              </Button>
              <Button 
                onClick={() => setEditModal({ open: true, pkg: null })}
                className="bg-gradient-to-r from-purple-600 to-pink-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('packageCatalog.header.create')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 py-6 pb-28">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">{allPackages.length}</p>
                <p className="text-sm text-gray-500">{t('packageCatalog.stats.total')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{availablePackages.length}</p>
                <p className="text-sm text-gray-500">{t('packageCatalog.stats.activePublic')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">{allPackages.filter(p => !p.active).length}</p>
                <p className="text-sm text-gray-500">{t('packageCatalog.stats.inactive')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{allPackages.filter(p => !p.public).length}</p>
                <p className="text-sm text-gray-500">{t('packageCatalog.stats.private')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Package List */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle>{t('packageCatalog.listTitle')}</CardTitle>
                <CardDescription>
                  {t('packageCatalog.listSubtitle')}
                </CardDescription>
              </div>
              {/* PKG-CATALOG-CREATE-01: canonical Create Package action, always
                  available (incl. embedded merged-admin view where the standalone
                  header — and its create button — is hidden). Same handler → same
                  canonical POST /packages writer. */}
              <Button
                size="sm"
                onClick={() => setEditModal({ open: true, pkg: null })}
                className="bg-gradient-to-r from-purple-600 to-pink-600 shrink-0"
                data-testid="package-create-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('packageCatalog.header.create')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* PKG-CATALOG-ADMIN-VISIBILITY-01: visibility/status filters */}
            <div className="flex flex-wrap gap-2 mb-4" data-testid="package-catalog-filters">
              {['all', 'public', 'hidden', 'inactive'].map((f) => (
                <Button
                  key={f}
                  type="button"
                  size="sm"
                  variant={filter === f ? 'default' : 'outline'}
                  className={filter === f ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  onClick={() => setFilter(f)}
                  data-testid={`package-filter-${f}`}
                >
                  {t(`packageCatalog.filters.${f}`)}
                </Button>
              ))}
            </div>
            {displayedPackages.length === 0 && (
              <p className="text-sm text-gray-500 py-8 text-center" data-testid="package-catalog-empty">
                {t('packageCatalog.empty')}
              </p>
            )}
            <div className="space-y-4">
              {displayedPackages.map(pkg => (
                <div 
                  key={pkg.id}
                  className={`border rounded-lg p-4 ${
                    pkg.active && pkg.public 
                      ? 'bg-white border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{pkg.displayName}</h3>
                        <Badge variant="outline">v{pkg.version}</Badge>
                        {pkg.active ? (
                          <Badge className="bg-green-100 text-green-700">{t('packageCatalog.status.active')}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-400">{t('packageCatalog.status.inactive')}</Badge>
                        )}
                        {pkg.public ? (
                          <Badge className="bg-blue-100 text-blue-700">{t('packageCatalog.status.public')}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-400">{t('packageCatalog.status.private')}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{getLocalizedField(pkg.description, i18n.language)}</p>
                      <div className="flex items-center gap-6 text-sm text-gray-500">
                        {(() => {
                          const cov = priceCoverage[pkg.packageKey || pkg.package_key || pkg.id];
                          const markets = cov?.markets || [];
                          const published = markets.filter((m) => m.has_published_price);
                          if (published.length === 0) {
                            return (
                              <span className="font-medium text-amber-600" data-testid={`price-coverage-${pkg.id}`}>
                                {t('packageCatalog.meta.noMarketPrice', 'No published market price')}
                              </span>
                            );
                          }
                          return (
                            <span className="flex items-center gap-1.5" data-testid={`price-coverage-${pkg.id}`}>
                              {published.map((m) => (
                                <Badge key={m.market} variant="outline" className="text-xs font-medium text-green-700 border-green-300">
                                  {m.market}/{m.currency}
                                </Badge>
                              ))}
                              {cov?.missing_tr_try && (
                                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                  {t('packageCatalog.meta.noTrPrice', 'No TR/TRY')}
                                </Badge>
                              )}
                            </span>
                          );
                        })()}
                        <span>{t('packageCatalog.meta.leads')}: {(pkg.limits?.leads_per_month ?? 0) === -1 ? '\u221E' : pkg.limits?.leads_per_month ?? '-'}</span>
                        <span>{t('packageCatalog.meta.includedUsers')}: {(pkg.limits?.max_users ?? pkg.limits?.sales_reps ?? 0) === -1 ? '\u221E' : pkg.limits?.max_users ?? pkg.limits?.sales_reps ?? '-'}</span>
                        <span>{t('packageCatalog.meta.offers')}: {(pkg.limits?.offers_per_month ?? 0) === -1 ? '\u221E' : pkg.limits?.offers_per_month ?? '-'}</span>
                        <span>{t('packageCatalog.meta.quotes', 'Quote requests')}: {(pkg.limits?.quotes_monthly_max ?? 0) === -1 ? '\u221E' : pkg.limits?.quotes_monthly_max ?? '-'}</span>
                      </div>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {pkg.features?.leads_enabled && <Badge variant="outline" className="text-xs">{t('packageCatalog.features.leadsManagement')}</Badge>}
                        {pkg.features?.campaigns_enabled && <Badge variant="outline" className="text-xs">{t('packageCatalog.features.campaigns')}</Badge>}
                        {pkg.features?.priority_support && <Badge variant="outline" className="text-xs text-gray-500">{t('packageCatalog.features.prioritySupport')}</Badge>}
                        {pkg.features?.dedicated_support && <Badge variant="outline" className="text-xs text-gray-500">{t('packageCatalog.features.dedicatedSupport')}</Badge>}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs">
                        <span className="text-gray-500">{t('packageCatalog.meta.upgradeOrder')}: <strong>{pkg.upgrade_order ?? pkg.sortOrder ?? '?'}</strong></span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => requestToggleActive(pkg)}
                              data-testid={`package-toggle-active-${pkg.id}`}
                              aria-label={pkg.active ? t('packageCatalog.tooltips.deactivate') : t('packageCatalog.tooltips.activate')}
                            >
                              {pkg.active ? <Power className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {pkg.active ? t('packageCatalog.tooltips.deactivate') : t('packageCatalog.tooltips.activate')}
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => requestTogglePublic(pkg)}
                              data-testid={`package-toggle-public-${pkg.id}`}
                              aria-label={pkg.public ? t('packageCatalog.tooltips.hide') : t('packageCatalog.tooltips.show')}
                            >
                              {pkg.public ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {pkg.public ? t('packageCatalog.tooltips.hide') : t('packageCatalog.tooltips.show')}
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditModal({ open: true, pkg })}
                              data-testid={`package-edit-${pkg.id}`}
                              aria-label={t('packageCatalog.tooltips.edit')}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t('packageCatalog.tooltips.edit')}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const key = pkg.packageKey || pkg.package_key || pkg.id;
                                const uuid = priceCoverage[key]?.package_id || pkg.doc_id || pkg.uuid;
                                setPricesModal({ open: true, pkg, packageId: uuid });
                              }}
                              data-testid={`package-manage-prices-${pkg.id}`}
                              aria-label={t('packageCatalog.prices.manage', 'Manage prices')}
                            >
                              <Tag className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t('packageCatalog.prices.manage', 'Manage prices')}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Info Note */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">{t('packageCatalog.note.title')}</h4>
              <p className="text-sm text-blue-700 mt-1">
                {t('packageCatalog.note.body')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* PKG-CATALOG-ADMIN-VISIBILITY-01: confirmation for high-impact actions */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent data-testid="package-confirm-dialog" className="z-[10000]">
          {confirmAction && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle data-testid="package-confirm-title">
                  {t(`packageCatalog.confirm.${confirmAction.type}.title`)}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(`packageCatalog.confirm.${confirmAction.type}.body`)}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="package-confirm-cancel">
                  {t('packageCatalog.confirm.cancel')}
                </AlertDialogCancel>
                <AlertDialogAction onClick={executeConfirm} data-testid="package-confirm-accept">
                  {t(`packageCatalog.confirm.${confirmAction.type}.confirm`)}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Modal */}
      {editModal.open && (
        <PackageEditorModal
          pkg={editModal.pkg}
          onSave={handleSavePackage}
          onClose={() => setEditModal({ open: false, pkg: null })}
        />
      )}

      {/* CANONICAL SSOT: Commercial Price Manager */}
      {pricesModal.open && pricesModal.pkg && pricesModal.packageId && (
        <PriceManagerModal
          pkg={pricesModal.pkg}
          packageId={pricesModal.packageId}
          onClose={() => setPricesModal({ open: false, pkg: null })}
          onChanged={fetchCoverage}
        />
      )}

      {/* Reset Confirmation Modal */}
      {showConfirmReset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                {t('packageCatalog.reset.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                {t('packageCatalog.reset.body')}
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowConfirmReset(false)}>
                  {t('packageCatalog.confirm.cancel')}
                </Button>
                <Button variant="destructive" onClick={handleResetToDefaults}>
                  {t('packageCatalog.reset.confirm')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ClinicCRMPackageManagementPage;
